import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import VersionHistory from "@/components/admin/VersionHistory";
import { api } from "@/lib/api";
import { tr } from "@/i18n/translate";

// Complete / Missing N / Empty for one language of one doc (from the i18n overview or side-by-side status).
export function StatusBadge({ s, testId }) {
  if (!s) return null;
  if (s.status === "complete") return <Pill tone="success" data-testid={testId}>{tr("Complete")}</Pill>;
  if (s.status === "empty") return <Pill tone="error" data-testid={testId}>{tr("Empty")}</Pill>;
  return <Pill tone="warning" data-testid={testId}>{tr(`Missing ${s.missing_keys || ""}`.trim())}</Pill>;
}
export const needsTranslation = (s) => !!s && (s.status !== "complete" || s.stale);

// One non-English column: its own text, version history and save. English stays the master in the EN column.
export default function TranslationColumn({ doc, kind, legal, lang, name, s, isOwner, onSaved }) {
  const [title, setTitle] = useState(s?.title || "");
  const [body, setBody] = useState(s?.body || "");
  const [reviewed, setReviewed] = useState(false);
  const [ask, setAsk] = useState(false);
  const [hist, setHist] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const id = `tr-${lang}`;
  const dirty = body !== (s?.body || "") || title !== (s?.title || "");
  const kv = kind === "kv" || kind === "list";
  const empty = s?.status === "empty";

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const res = await api(`/admin/i18n/content/${doc}/${lang}`, { body: { title: title.trim() || null, body, ...(legal ? { reviewed: isOwner && reviewed } : {}) } });
      toast.success(tr(`Saved ${name} as version ${res?.content?.version ?? ""}.`));
      (res?.warnings || []).forEach((w) => toast.warning(`${w.key}: ${w.message}`));
      setReviewed(false); setHist((k) => k + 1); onSaved();
    } catch (e) { setError(e.message); toast.error(e.message); } finally { setBusy(false); }
  };
  const clearReview = async () => {
    await api(`/admin/i18n/content/${doc}/${lang}/clear-review`, { method: "POST" });
    toast.success(tr("Marked reviewed. It's now served in this language."));
    setHist((k) => k + 1); onSaved();
  };

  return (
    <div className="min-w-0 space-y-6" data-testid={`translation-column-${lang}`}>
      <Panel className={`p-5 ${empty ? "ring-2 ring-red-300" : s?.status === "partial" ? "ring-2 ring-amber-300/60" : ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-auto font-semibold" data-no-i18n>{lang.toUpperCase()} · {name}</h2>
          <StatusBadge s={s} testId={`translation-status-${lang}`} />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          {s?.draft_needs_review && <Pill tone="warning" data-testid={`translation-needs-review-${lang}`}>Draft — needs review</Pill>}
          {s?.source && <Pill data-testid={`translation-source-${lang}`}>{s.source === "ai" ? "AI draft" : "Edited by a person"}</Pill>}
          {s?.stale && <Pill tone="warning" data-testid={`translation-stale-${lang}`}>English changed since</Pill>}
          {s?.version != null && <span className="cw-muted self-center tabular-nums">v{s.version}</span>}
        </div>
        {s?.draft_needs_review && isOwner && (
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setAsk(true)} data-testid={`translation-mark-reviewed-${lang}`}>Mark reviewed</Button>
        )}
        {!!s?.missing?.length && (
          <div className="mt-4 rounded-xl bg-amber-50 p-3 text-xs" data-testid={`translation-missing-${lang}`}>
            <p className="font-medium">Missing keys (English shows for these):</p>
            <p className="mt-1 break-words font-mono" data-no-i18n>{s.missing.join(", ")}</p>
          </div>
        )}
        {kind === "prose" && (
          <div className="mt-4"><Label htmlFor={`${id}-title`}>Title</Label>
            <Input id={`${id}-title`} value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2" data-testid={`translation-title-input-${lang}`} /></div>
        )}
        <div className="mt-4">
          <Label htmlFor={`${id}-body`}>Body</Label>
          {kv && <p className="cw-muted mt-1 text-xs">One “key: text” per line. Only include the keys you translate; the rest show English.</p>}
          {empty && <p className="cw-error mt-1 text-xs">Empty: English shows until you add text.</p>}
          <Textarea id={`${id}-body`} value={body} onChange={(e) => { setBody(e.target.value); setError(null); }} rows={kv ? 12 : 14}
            aria-invalid={!!error} className={`mt-2 text-sm leading-6 ${kv ? "font-mono" : ""} ${empty || error ? "border-[var(--cw-error)]" : ""}`} data-testid={`translation-body-input-${lang}`} />
          {error && <p className="cw-error mt-2 text-sm" role="alert" data-testid={`translation-save-error-${lang}`}>{error}</p>}
        </div>
        {legal && isOwner && (
          <label htmlFor={`${id}-reviewed`} className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl bg-[var(--cw-surface-tertiary)] p-3">
            <Checkbox id={`${id}-reviewed`} checked={reviewed} onCheckedChange={(v) => setReviewed(!!v)} className="mt-0.5" data-testid={`translation-reviewed-checkbox-${lang}`} />
            <span><span className="block text-sm font-medium">Reviewed by a person (goes live)</span>
              <span className="cw-soft block text-xs leading-5">{"Legal text: without this, members keep seeing English until it's marked reviewed."}</span></span>
          </label>
        )}
        <div className="mt-5 flex items-center justify-end gap-3">
          {dirty && <Pill tone="brand">Unsaved</Pill>}
          <Button onClick={save} disabled={busy || !dirty || !body.trim()} data-testid={`translation-save-button-${lang}`}>{busy ? "Saving…" : "Save translation"}</Button>
        </div>
      </Panel>
      <VersionHistory doc={doc} path={`/admin/i18n/content/${doc}/${lang}/versions`} refreshKey={hist} testId={`translation-history-${lang}`}
        onLoad={(v) => { setTitle(v.title || ""); setBody(v.body || ""); toast.message(tr(`Loaded v${v.version} into the editor. Save to publish it.`)); }} />
      <ConfirmDialog open={ask} onOpenChange={setAsk} destructive={false} testId={`translation-review-dialog-${lang}`}
        title="Mark this version reviewed?" description="Members and the website get this version instead of the English one." confirmLabel="Mark reviewed" onConfirm={clearReview} />
    </div>
  );
}
